import { ref, watch, onUnmounted, unref } from 'vue'
import { useUserStore } from '@/stores/userStore'
import { useAuthenticationStore } from '@/stores/useAuthenticationStore'
import { useVersionStore } from '@/stores/versionStore'
import { sendHeartbeat, endSession } from '@/api/sessionApi'

// Shared interval ID to allow multiple components to use the tracker without spawning multiple timers
let heartbeatInterval: number | null = null
const currentStepName = ref<string>('')
let lastHumanActivityTime = Date.now()
let stepStartedAt = Date.now()
let activeTrackerCount = 0

// Unique ID for this specific browser tab/window to prevent multi-tab flickering in admin view
// Using sessionStorage to ensure the ID persists through page reloads in the same tab
const getTabId = () => {
    if (typeof window === 'undefined') return Math.random().toString(36).substring(2, 10)
    
    let id = sessionStorage.getItem('dn_session_tab_id')
    if (!id) {
        id = Math.random().toString(36).substring(2, 10)
        sessionStorage.setItem('dn_session_tab_id', id)
    }
    return id
}
const tabId = getTabId()

// Unique ID for this browser session (persists across tabs, expires after 24h of inactivity)
const getBrowserSessionId = () => {
    if (typeof document === 'undefined') return ''
    
    const name = 'dn_browser_session_id'
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    let id = match ? match[2] : null
    
    if (!id) {
        id = Math.random().toString(36).substring(2, 15)
    }
    
    // Set/Refresh cookie to expire in 24 hours
    const date = new Date()
    date.setTime(date.getTime() + (24 * 60 * 60 * 1000))
    document.cookie = `${name}=${id}; expires=${date.toUTCString()}; path=/; SameSite=Lax`
    
    return id
}
const browserSessionId = getBrowserSessionId()

// Track save state
let hasAutoSaved = false
let manualSaveCount = 0

// Configuration
// Use 1 minute for staging/localhost, 30 minutes for production
const isDevEnvironment = typeof window !== 'undefined' && 
  (window.location.hostname.includes('localhost') || 
   window.location.hostname.includes('127.0.0.1') ||
   window.location.hostname.includes('staging'))
const AUTO_SAVE_IDLE_THRESHOLD = isDevEnvironment ? 1 * 60 * 1000 : 30 * 60 * 1000
const AUTO_SAVE_STEPS = ['createEventStep2', 'createEventStep3']
// BacklogTrace: production-func003-api-saturation — reduce API heartbeat load on func003
const SESSION_HEARTBEAT_INTERVAL_MS = isDevEnvironment ? 15000 : 60000
const IDLE_HEARTBEAT_SKIP_SECONDS = isDevEnvironment ? 120 : 60

export function useSessionTracker(eventData?: any, saveCallback?: () => Promise<void>) {
    const userStore = useUserStore()
    const authStore = useAuthenticationStore()
    const isTracking = ref(false)

    // Track human activity
    function trackActivity() {
        lastHumanActivityTime = Date.now()
    }
    
    // Setup activity listeners
    function setupActivityListeners() {
        const events = ['mousedown', 'keydown', 'touchstart', 'click']
        const throttledTrackActivity = () => {
            trackActivity()
        }
        
        events.forEach(event => {
            window.addEventListener(event, throttledTrackActivity)
        })
    }
    
    // Check if auto-save is needed
    async function checkAndAutoSave() {
        if (!AUTO_SAVE_STEPS.includes(currentStepName.value)) {
            return
        }
        
        // CRITICAL: Skip auto-save if version update is active (it handles saving)
        const versionStore = useVersionStore()
        if (versionStore.updateAvailable && !versionStore.dismissed) {
            return
        }
        
        const timeSinceStepStart = Date.now() - stepStartedAt
        
        // Auto-save if user has been on this step for threshold duration (regardless of activity)
        if (timeSinceStepStart >= AUTO_SAVE_IDLE_THRESHOLD && saveCallback) {
            try {
                // Handle Ref unwrapping if eventData is a ref
                const eventObject = unref(eventData)

                // IMPORTANT: Set default title BEFORE save so it gets persisted to backend
                if (eventObject && !eventObject.title) {
                    const now = new Date()
                    const dateStr = `${now.getDate()}-${now.getMonth() + 1}-${now.getFullYear()}`
                    eventObject.title = `Unavngivet arrangement ${dateStr}`
                }
                
                // Silent auto-save
                await saveCallback()
                
                if (!eventObject?.title) {
                    // Fallback: ensure title is set if it somehow got cleared
                    const now = new Date()
                    const dateStr = `${now.getDate()}-${now.getMonth() + 1}-${now.getFullYear()}`
                    eventObject.title = `Unavngivet arrangement ${dateStr}`
                }
                
                // Mark auto-save in session storage for modal on next load
                sessionStorage.setItem('autoSavedEventDraft', 'true')
                sessionStorage.setItem('autoSaveTime', new Date().toISOString())
                
                // Update save state for next heartbeat
                hasAutoSaved = true
                
                // Reset step timer to prevent immediate re-triggering
                // But allow modal to reappear if user dismisses and continues
                stepStartedAt = Date.now()
                
                // Send immediate heartbeat with auto-save state
                sendSessionHeartbeat()
                
                // Show modal if component is listening, pass event title (guaranteed to be set now)
                const eventTitle = eventObject?.title || 'Unavngivet arrangement'
                window.dispatchEvent(new CustomEvent('auto-save-completed', { detail: { title: eventTitle } }))
                
                // Keep session active - do NOT call stopTracking()
            } catch (error) {
                console.error('Auto-save failed:', error)
            }
        }
    }
    
    // Get user identity
    function getUserInfo() {
        const userId = authStore.userId || 'anonymous-' + getAnonymousId()
        
        let userName = 'Anonymous User'
        if (userStore.user?.contact?.firstName) {
            userName = `${userStore.user.contact.firstName} ${userStore.user.contact.lastName}`.trim()
        } else if (userStore.user?.email) {
            userName = userStore.user.email
        }
        
        return { userId, userName }
    }

    function getAnonymousId() {
        let id = localStorage.getItem('anonymous_session_id')
        if (!id) {
            id = Math.random().toString(36).substring(2, 15)
            localStorage.setItem('anonymous_session_id', id)
        }
        return id
    }

    function trackManualSave() {
        manualSaveCount++
        // Send immediate heartbeat to reflect manual save
        sendSessionHeartbeat()
    }

    // Helper function to send heartbeat with current session state
    function sendSessionHeartbeat() {
        const isAutoSaveStep = AUTO_SAVE_STEPS.includes(currentStepName.value)
        const idleDurationSeconds = Math.floor((Date.now() - lastHumanActivityTime) / 1000)

        // Skip noisy heartbeats when the user is idle on non-wizard routes (browse/search).
        if (!isAutoSaveStep && idleDurationSeconds > IDLE_HEARTBEAT_SKIP_SECONDS) {
            return
        }

        const { userId, userName } = getUserInfo()
        // @ts-ignore
        const appVersion = window.__APP_VERSION__ || 'unknown'
        
        // Only track save state on Step 2/3, clear it on all other routes
        const autoSavedStatus = isAutoSaveStep ? hasAutoSaved : false
        const saveCountStatus = isAutoSaveStep ? manualSaveCount : 0
        
        // Calculate durations
        const now = Date.now()
        const routeDurationSeconds = Math.floor((now - stepStartedAt) / 1000)

        sendHeartbeat(userId, userName, currentStepName.value, appVersion, autoSavedStatus, saveCountStatus, idleDurationSeconds, routeDurationSeconds, tabId, browserSessionId)
    }

    function startTracking(stepName: string) {
        if (!isTracking.value) {
            activeTrackerCount++
            isTracking.value = true
        }
        
        currentStepName.value = stepName
        stepStartedAt = Date.now()
        lastHumanActivityTime = Date.now()
        
        // Reset save tracking - only keep state when on auto-save steps
        if (!AUTO_SAVE_STEPS.includes(stepName)) {
            hasAutoSaved = false
            manualSaveCount = 0
        }
        
        // Setup activity listeners on first start
        if (!heartbeatInterval) {
            setupActivityListeners()
        }
        
        // Send immediate heartbeat
        sendSessionHeartbeat()

        // Start interval if not running
        if (!heartbeatInterval) {
            heartbeatInterval = window.setInterval(() => {
                if (currentStepName.value) {
                    sendSessionHeartbeat()
                    
                    // Check for auto-save (only on auto-save steps)
                    checkAndAutoSave()
                }
            }, SESSION_HEARTBEAT_INTERVAL_MS)
        }
    }

    function updateStep(stepName: string) {
        currentStepName.value = stepName
        sendSessionHeartbeat()
    }

    function stopTracking() {
        if (!isTracking.value) return
        
        activeTrackerCount--
        isTracking.value = false
        
        if (activeTrackerCount > 0) {
            return // Other components are still using the tracker
        }

        currentStepName.value = ''
        hasAutoSaved = false
        manualSaveCount = 0
        if (heartbeatInterval) {
            clearInterval(heartbeatInterval)
            heartbeatInterval = null
        }
        
        // Cleanup activity listeners
        const events = ['mousedown', 'keydown', 'touchstart', 'click']
        events.forEach(event => {
            window.removeEventListener(event, () => trackActivity())
        })
        
        const { userId } = getUserInfo()
        endSession(userId, tabId, browserSessionId)
    }

    return {
        startTracking,
        updateStep,
        stopTracking,
        trackManualSave,
        tabId
    }
}

